/**
 * Rolling stock in use — the Brief's register of named engines (Chris, 2026-09-11).
 *
 * Every locomotive and unit the railway can put out, NYMR-owned or contracted,
 * with the two switches the build obeys and the contract shape the Cost model
 * stage prices:
 *   - USE IN SIMULATION: a ticked engine is in the fleet — every working turn
 *     is given a named engine from the ticked ones (fleetRegister.ts::
 *     fleetCapsFrom caps the Brief's daily counts at the ticked engines, never
 *     raising them) — and gets its own named lines under Hire of Rolling Stock
 *     on the Cost model stage; unticked, it is out of the fleet and off the costs.
 *   - ORDER IN THE LIST is also the order engines are picked for turns: a Whitby
 *     turn takes the first free Whitby-enabled engine of its traction; any other
 *     turn takes the first free engine, preferring one not needed for Whitby.
 *   - WHITBY ENABLED: a diagram that touches Whitby (Network Rail) is given only
 *     an engine with this ticked; with none free the diagram is reported on the
 *     Brief review, never quietly run.
 * Contract fields, all optional: £ per TIMETABLED mile (a chargeable mile is a
 * timetabled mile — the model's empty-stock moves carry no hire), £ per
 * operating day, a fixed £ a year, a contracted minimum of miles (payable at
 * zero), maintenance £ per mile (normally blank — NYMR pays maintenance, coal,
 * oil and crew through the P&L lines already, so a hire rate here is pure hire).
 * Basis miles: the COO's actual for the basis season; blank = estimated from
 * the timetable the railway ran.
 *
 * Stored in app.StudioLoco (db/83) via /api/research/locos. The orchestrator
 * owns the list and re-reads it after every write (fetch, await, save, render).
 */
import { Fragment, useState } from 'react'
import type { FleetLoco, OutPeriod, Traction } from '../types'
import { Btn, Callout, Field } from '../ui'
import { blankLoco, createLoco, deleteLoco, updateLoco, type LocoInput } from '../locoApi'
import { engineLabel } from '../fleetRegister'
import { contractOrder, normalisePeriods } from '../fleetAvailability'

export interface RollingStockPanelProps {
  locos: FleetLoco[]
  loading: boolean
  err: string | null
  /** Re-read the register after a write — the orchestrator owns the list. */
  onReload: () => void
}

type Draft = LocoInput & { id?: number }

const TRACTION: { value: Traction; label: string }[] = [
  { value: 'steam', label: 'Steam' }, { value: 'diesel', label: 'Diesel' }, { value: 'railcar', label: 'Railcar' },
]
const FUEL: { value: FleetLoco['fuel']; label: string }[] = [
  { value: 'coal', label: 'Coal' }, { value: 'oil', label: 'Oil' }, { value: 'diesel', label: 'Diesel' },
]
const NR_ITEMS: { key: keyof FleetLoco['nr']; label: string }[] = [
  { key: 'tpws', label: 'TPWS' }, { key: 'aws', label: 'AWS' }, { key: 'otmr', label: 'OTMR' }, { key: 'gsmr', label: 'GSM-R' }, { key: 'route', label: 'Route conducted' },
]

const money = (v: number | null | undefined, unit: string): string => (v == null || v === 0 ? '—' : `£${v >= 100 ? Math.round(v).toLocaleString('en-GB') : v.toFixed(2)}${unit}`)
const miles = (v: number | null | undefined): string => (v == null || v === 0 ? '—' : `${Math.round(v).toLocaleString('en-GB')} mi`)
const msg = (e: unknown): string => (e instanceof Error ? e.message : String(e))

/** Everything the API takes — the full row, so a PUT never blanks a field it was not sent. */
const toInput = (l: FleetLoco): LocoInput => ({
  number: l.number, name: l.name, owner: l.owner, ownership: l.ownership, traction: l.traction, fuel: l.fuel, seats: l.seats,
  whitbyEnabled: l.whitbyEnabled, costPerMile: l.costPerMile, dayRate: l.dayRate, fixedAnnualCost: l.fixedAnnualCost,
  maintenancePerMile: l.maintenancePerMile, fuelPerMile: l.fuelPerMile ?? null, minContractMiles: l.minContractMiles, basisMiles: l.basisMiles, failRate: l.failRate,
  activeFrom: l.activeFrom, activeTo: l.activeTo, washoutSeed: l.washoutSeed, washoutCycleDays: l.washoutCycleDays,
  washoutLockoutDays: l.washoutLockoutDays, boilerCertExpiry: l.boilerCertExpiry, examNotes: l.examNotes, nr: { ...l.nr },
  inSimulation: l.inSimulation, notes: l.notes, sortOrder: l.sortOrder,
  unavailable: (l.unavailable ?? []).map((p) => ({ ...p })),
})

export function RollingStockPanel({ locos, loading, err, onReload }: RollingStockPanelProps) {
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [confirmDel, setConfirmDel] = useState<number | null>(null)

  const rows = [...locos].sort((a, b) => (a.sortOrder - b.sortOrder) || a.number.localeCompare(b.number))
  const inUse = rows.filter((l) => l.inSimulation)
  const count = (t: Traction) => inUse.filter((l) => l.traction === t).length
  const whitby = inUse.filter((l) => l.whitbyEnabled)

  /** A tick changed in the table — save the whole row at once. */
  const quick = async (l: FleetLoco, patch: Partial<LocoInput>) => {
    setBusy(true); setSaveErr(null)
    try { await updateLoco(l.id, { ...toInput(l), ...patch }); onReload() } catch (e) { setSaveErr(msg(e)) } finally { setBusy(false) }
  }
  const save = async () => {
    if (!draft) return
    if (!draft.number.trim()) { setSaveErr('The engine needs its number (or unit numbers).'); return }
    setBusy(true); setSaveErr(null)
    const { id, ...rest } = draft
    // Out-of-service periods: both dates, in order, the reason trimmed (half-typed rows are dropped).
    const input: LocoInput = { ...rest, unavailable: normalisePeriods(rest.unavailable) }
    try {
      if (id != null) await updateLoco(id, input); else await createLoco(input)
      setDraft(null); onReload()
    } catch (e) { setSaveErr(msg(e)) } finally { setBusy(false) }
  }
  /** Contract order (Chris, 2026-09-11): a contracted engine runs up to its minimum
   *  before another is hired per mile — rewrite the list order, which stays the one
   *  owner of pick order; the planner can still move engines afterwards. */
  const orderByContract = async () => {
    setBusy(true); setSaveErr(null)
    try {
      const ordered = contractOrder(rows)
      for (let i = 0; i < ordered.length; i++) {
        const want = (i + 1) * 10
        if (ordered[i].sortOrder !== want) await updateLoco(ordered[i].id, { ...toInput(ordered[i]), sortOrder: want })
      }
      onReload()
    } catch (e) { setSaveErr(msg(e)) } finally { setBusy(false) }
  }
  const setPeriod = (i: number, p: Partial<OutPeriod>) => setDraft((d) => (d ? { ...d, unavailable: d.unavailable.map((x, k) => (k === i ? { ...x, ...p } : x)) } : d))
  const remove = async (id: number) => {
    setBusy(true); setSaveErr(null)
    try { await deleteLoco(id); setConfirmDel(null); onReload() } catch (e) { setSaveErr(msg(e)) } finally { setBusy(false) }
  }
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d))
  const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number.isFinite(Number(s)) ? Number(s) : null)
  const textOrNull = (s: string): string | null => (s.trim() === '' ? null : s)

  // The engine form. Edit opens it IN PLACE, under the engine's own row
  // (Chris, 2026-10-28: scrolling up and down to edit was "incredibly
  // unfriendly"); Add an engine opens it under the list.
  const editor = draft ? (
    <div className="sh-card sh-card--pad sh-mt" style={{ background: 'var(--card, #fff)' }}>
      <div className="sh-section-label">{draft.id != null ? `Edit ${draft.number}` : 'Add an engine'}</div>
      <div className="stu-grid">
        <Field label="Number"><input type="text" value={draft.number} onChange={(e) => set('number', e.target.value)} placeholder="92134 · 51356/59486/51388" /></Field>
        <Field label="Name"><input type="text" value={draft.name ?? ''} onChange={(e) => set('name', textOrNull(e.target.value))} placeholder="Eric Treacy" /></Field>
        <Field label="Owner"><input type="text" value={draft.owner ?? ''} onChange={(e) => set('owner', textOrNull(e.target.value))} placeholder="NYMR · Riley & Son · Bury Hydraulics" /></Field>
        <Field label="Ownership">
          <select value={draft.ownership} onChange={(e) => set('ownership', e.target.value as Draft['ownership'])}>
            <option value="nymr">NYMR-owned</option><option value="contract">Contract hire</option>
          </select>
        </Field>
        <Field label="Traction">
          <select value={draft.traction} onChange={(e) => set('traction', e.target.value as Traction)}>
            {TRACTION.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Fuel" hint="Coal engines are the ones stood down in a fire-risk week; an oil-fired engine is not.">
          <select value={draft.fuel} onChange={(e) => set('fuel', e.target.value as Draft['fuel'])}>
            {FUEL.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </Field>
        <Field label="Seats" hint="Railcars only — the Autocar is 90.">
          <input type="number" min={0} value={draft.seats ?? ''} onChange={(e) => set('seats', numOrNull(e.target.value))} />
        </Field>
        <Field label="Whitby enabled" hint="May take a turn over Network Rail to Whitby. The one switch the build obeys for a Whitby turn.">
          <input type="checkbox" checked={draft.whitbyEnabled} onChange={() => set('whitbyEnabled', !draft.whitbyEnabled)} />
        </Field>
        <Field label="Network Rail items" hint="Recorded beneath the Whitby switch — the confirmations the clearance rests on.">
          <span style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {NR_ITEMS.map((x) => (
              <label key={x.key} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 12 }}>
                <input type="checkbox" checked={!!draft.nr?.[x.key]} onChange={() => set('nr', { ...draft.nr, [x.key]: !draft.nr?.[x.key] })} />{x.label}
              </label>
            ))}
          </span>
        </Field>
        <Field label="Cost per timetabled mile" value="£" hint="A chargeable mile is a timetabled mile; empty-stock moves carry no hire.">
          <input type="number" min={0} step={0.25} value={draft.costPerMile ?? ''} onChange={(e) => set('costPerMile', numOrNull(e.target.value))} />
        </Field>
        <Field label="Cost per operating day" value="£" hint="Charged for each day the engine works a turn.">
          <input type="number" min={0} step={25} value={draft.dayRate ?? ''} onChange={(e) => set('dayRate', numOrNull(e.target.value))} />
        </Field>
        <Field label="Fixed cost a year" value="£" hint="Payable regardless of miles — a flat fee. Spread across the running months.">
          <input type="number" min={0} step={500} value={draft.fixedAnnualCost ?? ''} onChange={(e) => set('fixedAnnualCost', numOrNull(e.target.value))} />
        </Field>
        <Field label="Maintenance per mile" value="£" hint="Only if the contract charges it on top — NYMR's own maintenance, coal, oil and crew are already in the P&L lines.">
          <input type="number" min={0} step={0.25} value={draft.maintenancePerMile ?? ''} onChange={(e) => set('maintenancePerMile', numOrNull(e.target.value))} />
        </Field>
        <Field label="Fuel & oils per mile" value="£" hint="This engine's own fuel and oils per timetabled mile. Set, it gets its own variable line on the Cost model and the same £ comes off the P&L fuel line; blank keeps it inside the P&L fuel rate.">
          <input type="number" min={0} step={0.25} value={draft.fuelPerMile ?? ''} onChange={(e) => set('fuelPerMile', numOrNull(e.target.value))} />
        </Field>
        <Field label="Minimum contract miles" value="mi" hint="The contract's minimum: the roster gives this engine its miles FIRST, spread over the season. It is paid on the miles it runs — not a fixed charge.">
          <input type="number" min={0} step={100} value={draft.minContractMiles ?? ''} onChange={(e) => set('minContractMiles', numOrNull(e.target.value))} />
        </Field>
        <Field label="Basis-season miles" value="mi" hint="The COO's actual for the basis season. Blank = the contracted minimum is assumed (we have no mileage return); an engine with no minimum is estimated from the timetable the railway ran. Type the actual here to replace either.">
          <input type="number" min={0} step={100} value={draft.basisMiles ?? ''} onChange={(e) => set('basisMiles', numOrNull(e.target.value))} />
        </Field>
        <Field label="Active from"><input type="date" value={draft.activeFrom ?? ''} onChange={(e) => set('activeFrom', textOrNull(e.target.value))} /></Field>
        <Field label="Active to"><input type="date" value={draft.activeTo ?? ''} onChange={(e) => set('activeTo', textOrNull(e.target.value))} /></Field>
        <Field label="Washout: a washout date" hint="Steam only. An engine is out for about a WEEK around each washout — cooling down, washing out, firing up — not just the washout day. This date fixes the cycle: the washouts repeat every cycle before AND after it, so a 2027 date also places the 2026 washouts.">
          <input type="date" value={draft.washoutSeed ?? ''} onChange={(e) => set('washoutSeed', textOrNull(e.target.value))} />
        </Field>
        <Field label="Washout cycle" value="days"><input type="number" min={1} value={draft.washoutCycleDays ?? ''} onChange={(e) => set('washoutCycleDays', numOrNull(e.target.value))} /></Field>
        <Field label="Washout lock-out" value="days" hint="Days the engine is unavailable around a washout (cooling, washing out, firing up).">
          <input type="number" min={0} value={draft.washoutLockoutDays ?? ''} onChange={(e) => set('washoutLockoutDays', numOrNull(e.target.value))} />
        </Field>
        <Field label="Boiler certificate expires"><input type="date" value={draft.boilerCertExpiry ?? ''} onChange={(e) => set('boilerCertExpiry', textOrNull(e.target.value))} /></Field>
        <Field label="Exams / notes on maintenance"><input type="text" value={draft.examNotes ?? ''} onChange={(e) => set('examNotes', textOrNull(e.target.value))} /></Field>
        <Field label="Out of service — dated periods" hint="Exams, repairs, an overhaul: the engine is not in the fleet on these dates. Washout weeks are generated from the washout date and cycle above; the boiler certificate and the active dates apply on their own.">
          <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {draft.unavailable.map((p, i) => (
              <span key={i} style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="date" value={p.from} onChange={(e) => setPeriod(i, { from: e.target.value })} />
                <span className="sh-dim" style={{ fontSize: 11 }}>to</span>
                <input type="date" value={p.to} onChange={(e) => setPeriod(i, { to: e.target.value })} />
                <input type="text" value={p.reason} placeholder="reason — exam, repair, overhaul" onChange={(e) => setPeriod(i, { reason: e.target.value })} style={{ width: 170 }} />
                <Btn kind="quiet" small title="Remove this period" onClick={() => set('unavailable', draft.unavailable.filter((_, k) => k !== i))}>×</Btn>
              </span>
            ))}
            <span><Btn kind="quiet" small onClick={() => set('unavailable', [...draft.unavailable, { from: '', to: '', reason: '' }])}>+ period</Btn></span>
          </span>
        </Field>
        <Field label="Notes"><input type="text" value={draft.notes ?? ''} onChange={(e) => set('notes', textOrNull(e.target.value))} placeholder="items still to confirm, the agreement's reference…" /></Field>
        <Field label="Order in the list" hint="Also the order engines are picked for turns: a Whitby turn takes the first free Whitby-enabled engine of its traction; any other turn the first free engine, preferring one not needed for Whitby. Put the engines you would send out first at the top.">
          <input type="number" min={0} value={draft.sortOrder} onChange={(e) => set('sortOrder', Number(e.target.value) || 0)} />
        </Field>
        <Field label="Use in simulation" hint="In the fleet and costed by name on the Cost model stage.">
          <input type="checkbox" checked={draft.inSimulation} onChange={() => set('inSimulation', !draft.inSimulation)} />
        </Field>
      </div>
      <div className="sh-mt" style={{ display: 'flex', gap: 8 }}>
        <Btn kind="gold" small disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : draft.id != null ? 'Save the engine' : 'Add to the register'}</Btn>
        <Btn kind="quiet" small disabled={busy} onClick={() => { setDraft(null); setSaveErr(null) }}>Cancel</Btn>
      </div>
    </div>
  ) : null

  return (
    <section className="sh-card sh-card--pad">
      <div className="sh-section-label">Rolling stock in use</div>
      <p className="stu-note" style={{ margin: '0 0 8px' }}>
        The railway&apos;s engines by name. Tick <strong>Use in simulation</strong> and an engine is in the fleet: every working
        turn is given a named engine from the ticked ones, and the engine&apos;s hire appears as its own lines under Hire of
        Rolling Stock on the Cost model stage, priced by its contract on the timetabled miles it works. How many engines are
        out on a day is still the Scope&apos;s count in Fleet &amp; sets (Model settings) — the register caps that count at the
        ticked engines and never raises it. Tick <strong>Whitby enabled</strong> and an engine may take a turn over Network
        Rail to Whitby; a Whitby turn with no enabled engine free is reported under ⚠ Interventions, never quietly run.
      </p>
      <p className="stu-note" style={{ margin: '0 0 8px' }}>
        {inUse.length > 0
          ? <>Ticked for the simulation: <strong>{count('steam')} steam, {count('diesel')} diesel, {count('railcar')} railcar</strong> ({inUse.map(engineLabel).join(', ')}).
              Whitby enabled: {whitby.length > 0 ? whitby.map(engineLabel).join(', ') : <span style={{ color: '#b03535' }}>none — no Whitby turn can be given an engine</span>}.</>
          : <>No engine is ticked — the Scope uses the plain counts in Fleet &amp; sets and engines are labelled Steam 1, Diesel 1.</>}
      </p>
      {err && <Callout kind="error" title="The register could not be loaded">{err}</Callout>}
      {saveErr && <Callout kind="error" title="Not saved">{saveErr}</Callout>}

      <div className="stu-scroll">
        <table className="stu-table">
          <thead>
            <tr>
              <th title="In the fleet and costed by name">Use</th>
              <th>Engine</th>
              <th>Owner</th>
              <th>Traction · fuel</th>
              <th className="num">Seats</th>
              <th title="May work a turn over Network Rail to Whitby">Whitby</th>
              <th className="num" title="£ per timetabled mile">£/mile</th>
              <th className="num" title="£ per operating day worked">£/day</th>
              <th className="num" title="Fixed £ a year, payable regardless">Fixed £/yr</th>
              <th className="num" title="Maintenance £ per mile on top of the P&L — normally blank">Maint £/mi</th>
              <th className="num" title="The engine's own fuel & oils £ per mile — its own Cost model line, off the P&L fuel">Fuel £/mi</th>
              <th className="num" title="Contracted minimum miles, payable at zero">Min miles</th>
              <th className="num" title="Actual miles in the basis season (COO); blank = the contracted minimum is assumed, or the timetable estimate where there is no minimum">Basis miles</th>
              <th title="Dated out-of-service periods (exams, repairs, overhaul). Washout weeks come from the washout date and cycle; 'w/o' marks an engine with them.">Out</th>
              <th title="Network Rail items confirmed">NR</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => {
              const nrDone = NR_ITEMS.filter((x) => l.nr?.[x.key]).length
              return (
                <Fragment key={l.id}>
                <tr style={l.inSimulation ? undefined : { opacity: 0.55 }}>
                  <td><input type="checkbox" checked={l.inSimulation} disabled={busy} onChange={() => void quick(l, { inSimulation: !l.inSimulation })} /></td>
                  <td>
                    <strong>{l.number}</strong>{l.name ? ` ${l.name}` : ''}
                    {l.notes && <div className="sh-dim" style={{ fontSize: 10, maxWidth: 260 }}>{l.notes}</div>}
                  </td>
                  <td>{l.owner ?? '—'}<div className="sh-dim" style={{ fontSize: 10 }}>{l.ownership === 'nymr' ? 'NYMR' : 'Contract'}</div></td>
                  <td>{l.traction}<span className="sh-dim"> · {l.fuel}</span></td>
                  <td className="num sh-mono">{l.seats ?? '—'}</td>
                  <td><input type="checkbox" checked={l.whitbyEnabled} disabled={busy} onChange={() => void quick(l, { whitbyEnabled: !l.whitbyEnabled })} /></td>
                  <td className="num sh-mono">{money(l.costPerMile, '')}</td>
                  <td className="num sh-mono">{money(l.dayRate, '')}</td>
                  <td className="num sh-mono">{money(l.fixedAnnualCost, '')}</td>
                  <td className="num sh-mono">{money(l.maintenancePerMile, '')}</td>
                  <td className="num sh-mono">{money(l.fuelPerMile ?? null, '')}</td>
                  <td className="num sh-mono">{miles(l.minContractMiles)}</td>
                  <td className="num sh-mono">
                    {l.basisMiles != null ? miles(l.basisMiles)
                      : (l.minContractMiles ?? 0) > 0 ? <span className="sh-dim" title="No COO mileage return — the contracted minimum is assumed; type the actual to replace it">min. {miles(l.minContractMiles)}</span>
                      : <span className="sh-dim" title="No minimum and no COO figure — estimated from the timetable the railway ran">est.</span>}
                  </td>
                  <td className="sh-mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                    title={[...(l.unavailable ?? []).map((p) => `${p.from} to ${p.to}: ${p.reason || 'out of service'}`), l.traction === 'steam' && l.washoutSeed && (l.washoutCycleDays ?? 0) > 0 ? `washout week every ${l.washoutCycleDays} days from ${l.washoutSeed} (${l.washoutLockoutDays ?? 7} days out each time)` : ''].filter(Boolean).join('\n') || 'no dated periods'}>
                    {(l.unavailable?.length ?? 0) > 0 ? `${l.unavailable.length} period${l.unavailable.length === 1 ? '' : 's'}` : '—'}
                    {l.traction === 'steam' && l.washoutSeed && (l.washoutCycleDays ?? 0) > 0 ? <span className="sh-dim"> · w/o</span> : null}
                  </td>
                  <td className="sh-mono" style={{ fontSize: 11 }} title={NR_ITEMS.map((x) => `${x.label}: ${l.nr?.[x.key] ? 'yes' : 'no'}`).join(' · ')}>{nrDone}/{NR_ITEMS.length}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Btn kind="quiet" small disabled={busy} onClick={() => { setSaveErr(null); setConfirmDel(null); setDraft({ ...toInput(l), id: l.id }) }}>Edit</Btn>{' '}
                    {confirmDel === l.id ? (
                      <>
                        <Btn kind="danger" small disabled={busy} onClick={() => void remove(l.id)}>Remove {l.number}</Btn>{' '}
                        <Btn kind="quiet" small onClick={() => setConfirmDel(null)}>Keep</Btn>
                      </>
                    ) : <Btn kind="quiet" small disabled={busy} onClick={() => setConfirmDel(l.id)}>Remove</Btn>}
                  </td>
                </tr>
                {draft?.id === l.id && (
                  <tr className="sh-loco-edit-row">
                    <td colSpan={16}><div className="sh-loco-edit">{editor}</div></td>
                  </tr>
                )}
                </Fragment>
              )
            })}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={16} className="stu-note">No engines in the register yet — add the fleet below.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="sh-mt" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Btn kind="gold" small disabled={busy || draft != null} onClick={() => { setSaveErr(null); setDraft({ ...blankLoco(), sortOrder: (rows.length + 1) * 10 }) }}>Add an engine</Btn>
        <Btn kind="quiet" small disabled={busy || draft != null || rows.length < 2} onClick={() => void orderByContract()}
          title="Rewrite the list order by contract: engines with a contracted minimum first (their miles are paid whether run or not, biggest minimum first), then owned engines, then per-mile hire cheapest first, then day-rate engines. The list order is the pick order for turns; move engines afterwards if you know better.">
          Order by contract
        </Btn>
        {loading && <span className="sh-dim">loading the register…</span>}
        {busy && <span className="sh-dim">saving…</span>}
      </div>

      {/* A new engine's form opens under the list; an existing engine's opens
          in place, under its own row. */}
      {draft && draft.id == null && editor}

      <p className="stu-note sh-mt" style={{ margin: '8px 0 0' }}>
        The register is the railway&apos;s, not this session&apos;s: a change here is seen by every Studio session at once, and a
        rebuild (★ Build Model) is needed for it to reach the figures. The washout date and cycle, the out-of-service
        periods, the boiler certificate and the active dates are the availability calendar: the build applies them DAY BY DAY
        (an engine out that day is not in the fleet; a steam turn no steam engine can take runs diesel; a turn no engine can
        take is reported) — see the fleet availability grid beneath.
      </p>
    </section>
  )
}
